import React, { useEffect, useState } from 'react';
import { Box, Fade } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import BodySwitchLogo from './svg/BodySwitchLogo';

// ============================================
// Styled Components - Overlay
// ============================================
const BlurOverlayContainer = styled(Box)({
  position: 'relative',
});

const BlurContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'isBlurred',
})(({ isBlurred }) => ({
  transition: 'filter 0.3s ease',
  ...(isBlurred && {
    filter: 'blur(4px)',
    pointerEvents: 'none',
    userSelect: 'none',
  }),
}));

const OverlayBackdrop = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  // 앱 헤더(MUI AppBar 1100)·사이드바(1200)·테이블 sticky 헤더보다 위에 표시
  zIndex: 1300,
});

const LightVersionModal = styled(Box)({
  // 감싸는 영역 크기와 무관하게 항상 화면 정가운데 표시
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1301,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '24px',
  padding: '48px 64px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
});

const LogoWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
});

const LogoBold = styled('span')({
  fontSize: '28px',
  fontWeight: 800,
  color: '#4AB3BC',
});

const LogoLight = styled('span')({
  fontSize: '28px',
  fontWeight: 100,
  color: '#4AB3BC',
});

const ModalTitle = styled('p')({
  fontSize: '20px',
  fontWeight: 600,
  color: '#262626',
  margin: 0,
  textAlign: 'center',
});

const ModalSubtitle = styled('p')({
  fontSize: '16px',
  fontWeight: 400,
  color: '#666',
  margin: 0,
  textAlign: 'center',
});

const InquiryButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '14px 48px',
  backgroundColor: '#0F7F85',
  borderRadius: '8px',
  cursor: 'pointer',
  '& p': {
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    margin: 0,
  },
  '&:hover': {
    backgroundColor: '#0d6a6f',
  },
});

// ============================================
// Hook
// ============================================
/**
 * useTransientLightVersionOverlay - 라이트버전 안내 오버레이를 잠깐 보여줬다 자동으로 닫는 훅
 * @param {number} duration - 표시 시간(ms), 기본 2500
 * @returns {{ active: boolean, show: () => void }}
 */
export const useTransientLightVersionOverlay = (duration = 2500) => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => setActive(false), duration);
    return () => clearTimeout(timer);
  }, [active, duration]);

  return { active, show: () => setActive(true) };
};

// ============================================
// Component
// ============================================
/**
 * LightVersionOverlay - ROLE_GUEST 사용자에게 라이트버전 안내 오버레이를 표시
 * @param {React.ReactNode} children - 블러 처리할 컨텐츠
 * @param {boolean} active - false면 블러/안내창 없이 children만 렌더 (일시 표시 용도, 기본 true)
 */
const LightVersionOverlay = ({ children, active = true }) => {
  const { t } = useTranslation();

  const handleInquiryClick = () => {
    window.open('https://www.bodyswitch.co.kr/', '_blank', 'noopener,noreferrer');
  };

  return (
    <BlurOverlayContainer>
      <BlurContent isBlurred={active}>{children}</BlurContent>
      <Fade in={active} unmountOnExit>
        <OverlayBackdrop>
          <LightVersionModal>
            <LogoWrapper>
              <BodySwitchLogo />
              <span>
                <LogoBold>BODY</LogoBold>
                <LogoLight>SWITCH</LogoLight>
              </span>
            </LogoWrapper>
            <ModalTitle>{t('현재 라이트버전을 사용중입니다.')}</ModalTitle>
            <ModalSubtitle>{t('문의를 남겨주세요')}</ModalSubtitle>
            <InquiryButton onClick={handleInquiryClick}>
              <p>{t('문의하기')}</p>
            </InquiryButton>
          </LightVersionModal>
        </OverlayBackdrop>
      </Fade>
    </BlurOverlayContainer>
  );
};

export default LightVersionOverlay;
